import { Box, Divider, Stack, Typography } from '@mui/material';
import { CalendarOutlined } from '@ant-design/icons';
import type { LibSubscription } from 'lib/billing/pbAdapter';
import { formatEndsOn } from 'sections/account-settings/subscriptions/checkout-v2/Configure/formatEndsOn';
import { Consequence, groupEndLabel, planGroups, sharedPeriodEnd } from '../billing';
import { CONFIRM_STEP, CONSEQUENCES } from '../content';
import { AcknowledgeCheck, Callout, CONSEQUENCE_ICONS, Group, GroupList, IconRow, PlanGroupRow } from './StepParts';

type Props = {
  subscriptions: LibSubscription[];
  catalog: object | null;
  consequences: Consequence[];
  acknowledged: boolean;
  onAcknowledgedChange: (acknowledged: boolean) => void;
  disabled: boolean;
};

const formatDate = (seconds: number) => formatEndsOn({ seconds });

/** When it all stops, what stops with it, and the one tick that unlocks the cancel. */
export default function CancellationConfirmStep({
  subscriptions,
  catalog,
  consequences,
  acknowledged,
  onAcknowledgedChange,
  disabled
}: Props) {
  const sharedEnd = sharedPeriodEnd(subscriptions);
  return (
    <Stack spacing={2.5}>
      <Callout icon={<CalendarOutlined />} tone="primary">
        <Typography variant="body2">
          {sharedEnd ? (
            <>
              {CONFIRM_STEP.activeUntilPrefix}{' '}
              <Box component="strong" sx={{ fontWeight: 600 }}>
                {formatDate(sharedEnd)}
              </Box>
            </>
          ) : (
            CONFIRM_STEP.activeUntilEach
          )}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {CONFIRM_STEP.undoNote}
        </Typography>
      </Callout>

      <GroupList>
        <Group label={CONFIRM_STEP.plansEnding}>
          {planGroups(subscriptions, catalog).map((group) => (
            <PlanGroupRow
              key={group.key}
              group={group}
              // One shared day is already in the callout; differing days are named per group.
              endsOn={sharedEnd ? null : groupEndLabel(group.periodEnds) || CONFIRM_STEP.periodEnd}
            />
          ))}
        </Group>
        {consequences.length > 0 && (
          <>
            <Divider />
            <Group label={CONFIRM_STEP.whatStops}>
              {consequences.map((consequence) => (
                <IconRow key={consequence} icon={CONSEQUENCE_ICONS[consequence]} tone="error" label={CONSEQUENCES[consequence]} />
              ))}
            </Group>
          </>
        )}
      </GroupList>

      <AcknowledgeCheck
        tone="error"
        label={CONFIRM_STEP.acknowledge}
        checked={acknowledged}
        onChange={onAcknowledgedChange}
        disabled={disabled}
      />
    </Stack>
  );
}
